import { gql } from "@apollo/client";
import { TransactionDetail, transactionDetailFragment } from "./transactionDetailFragment";
import {
  EnforcementIntentSummary,
  EnforcementIntentSummaryPark,
  enforcementIntentSummaryFragment,
} from "./enforcementIntentSummary";
import { Park, parkFragment } from "./parkFragment";

export interface EnforcementPolicyOption {
  id: string;
  code: string;
  description: string;
  type: string;
  violationType: string;
  additionalFeeAmountDue: number;
}

export const enforcementIntentDetailFragment = gql`
  fragment enforcementIntentDetailFragment on enforcement_intent {
    ...enforcementIntentSummaryFragment
    clientId: client_id
    payeeAccountId: payee_account_id
    payeeAccount: payee_account {
      id
      customer {
        id
        addressCity: address_city
        addressState: address_state
      }
    }
    voidedAt: voided_at
    noticeIndex: notice_index
    violationType: violation_type
    description
    baseAmountDue: base_amount_due
    additionalFeeAmountDue: additional_fee_amount_due
    escalationFeeAmountDue: escalation_fee_amount_due
    feeAmountWaived: fee_amount_waived
    officerInputNoticeNumberPrefix: officer_input_notice_number_prefix
    officerInputNoticeNumber: officer_input_notice_number
    roLookupStatus: ro_lookup_status
    roLookupSubmittedAt: ro_lookup_submitted_at
    roLookupCompletedAt: ro_lookup_completed_at
    initialLetterRequestedAt: initial_letter_requested_at
    initialLetterSentAt: initial_letter_sent_at
    isWarning: is_warning
    dueAt: due_at
    noticeContentUrl: notice_content_url
    vehicleMake: vehicle_make
    vehicleMakeAlias: client_workflow_vehicle_make_alias {
      identifiedValue: identified_value
      displayName: display_name
      alias
    }
    vehicleModel: vehicle_model
    vehicleColor: vehicle_color
    officerBadgeId: officer_badge_id
    officerName: officer_name
    sessions(order_by: [{ started_at: asc }]) {
      id
      cameraId: camera_id
      parkId: park_id
      park {
        ...parkFragment
      }
      doubleParkId: double_park_id
      # No id and no fragment here: app_user's allow list on double_parks is
      # only start_time/end_time (+ the *_local computed fields), and fields
      # outside the allow list fail the whole query silently.
      doublePark: double_park {
        startTime: start_time
        endTime: end_time
      }
      type
      transactionId: transaction_id
      transaction {
        ...transactionDetailFragment
      }
      licensePlateImageUrl: license_plate_image_url
      startImageUrl: start_image_url
      endImageUrl: end_image_url
      licensePlateCapturedAt: license_plate_captured_at
      startedAt: started_at
      endedAt: ended_at
      streetAddress: street_address
      violationImageUrl: violation_image_url
      violationAt: violation_at
    }
    payments(order_by: [{ paid_at: asc }]) {
      id
      paidAmount: paid_amount
      paidAt: paid_at
    }
    policy {
      workflowIntegrationConfig: workflow_integration_config {
        officerInputNoticeNumberPrefixRegex: officer_input_notice_number_prefix_regex
        officerInputNoticeNumberRegex: officer_input_notice_number_regex
        isDueDateRequired: is_due_date_required
        isDueDateEditEnabled: is_due_date_edit_enabled
        isVehicleMakeMappingRequired: is_vehicle_make_mapping_required
        isVoidSubmittedEnforcementIntentAllowed: is_void_submitted_enforcement_intent_allowed
        isApplyPaymentToEnforcementIntentAllowed: is_apply_payment_to_enforcement_intent_allowed
      }
      routingParameter: routing_parameter
      enforcementPolicyOptions: child_policies(
        where: { parent_policy: { routing_parameter: { _eq: user_input } } }
        order_by: [{ code: asc }]
      ) {
        id
        code
        description
        type
        violationType: violation_type
        additionalFeeAmountDue: additional_fee_amount_due
      }
    }
  }
  ${enforcementIntentSummaryFragment}
  ${transactionDetailFragment}
  ${parkFragment}
`;

export interface EnforcementIntentDetail extends EnforcementIntentSummary {
  clientId: string;
  payeeAccountId: string;
  payeeAccount: {
    customer: {
      addressCity: string;
      addressState: string;
    };
  };
  voidedAt: string | null;
  noticeIndex: number;
  description: string | null;
  baseAmountDue: number;
  additionalFeeAmountDue: number;
  escalationFeeAmountDue: number;
  feeAmountWaived: number;
  officerInputNoticeNumberPrefix: string | null;
  officerInputNoticeNumber: string | null;
  roLookupStatus: string; // TODO: enum
  roLookupSubmittedAt: string | null;
  roLookupCompletedAt: string | null;
  initialLetterRequestedAt: string | null;
  initialLetterSentAt: string | null;
  isWarning: boolean;
  dueAt: string | null;
  vehicleMake: string;
  vehicleMakeAlias: EnforcementIntentVehicleMakeAlias[];
  vehicleModel: string;
  vehicleColor: string;
  officerBadgeId: string | null;
  officerName: string | null;
  sessions: {
    id: string;
    cameraId: string;
    parkId: string | null;
    // Intersected with EnforcementIntentSummaryPark ({id, durationSec}) to satisfy
    // EnforcementIntentSummary's sessions[].park type, which this interface inherits. Those two
    // fields aren't selected by this fragment's own `park { ...parkFragment }` above — they're
    // only present because this document spreads ...enforcementIntentSummaryFragment (below),
    // whose `sessions` selection uses identical arguments and so GraphQL merges its
    // `park { id, durationSec }` into this same park selection at execution time. If that
    // spread is ever removed, or the two `sessions` selections stop matching exactly, this type
    // will keep claiming id/durationSec are present with no compile error, while they silently
    // stop being there at runtime.
    park: (Park & EnforcementIntentSummaryPark) | null;
    doubleParkId: string | null;
    // endTime is NULL while the double park is live — the still-in-violation
    // badge (AUT-5502) reads exactly that.
    doublePark: {
      startTime: string;
      endTime: string | null;
    } | null;
    type: string; // TODO: enum
    transactionId: string;
    transaction: TransactionDetail | null;
    licensePlateImageUrl: string;
    startImageUrl: string;
    endImageUrl: string | null;
    licensePlateCapturedAt: string | null;
    startedAt: string;
    endedAt: string | null;
    streetAddress: string;
    violationImageUrl: string | null;
    violationAt: string | null;
  }[];
  noticeContentUrl: string | null;
  payments: {
    id: string;
    paidAmount: number;
    paidAt: string;
  }[];
  policy: {
    workflowIntegrationConfig: {
      isVehicleMakeMappingRequired: boolean;
      officerInputNoticeNumberPrefixRegex?: string;
      officerInputNoticeNumberRegex?: string;
      isDueDateRequired: boolean;
      isDueDateEditEnabled: boolean;
      isVoidSubmittedEnforcementIntentAllowed: boolean;
      isApplyPaymentToEnforcementIntentAllowed: boolean;
    } | null;
    routingParameter: string | null;
    enforcementPolicyOptions: EnforcementPolicyOption[];
  };
}

export interface EnforcementIntentVehicleMakeAlias {
  identifiedValue: string;
  displayName: string;
  alias: string;
}
